import type { Vec3 } from "eutopia.ts";
import { container, createLayer, createLayerType, polygon } from "eutopia.ts";
import { $, effect, resolve, signal } from "signlets";

import { calculateBestGlideSpeed } from "../calculations";
import { range as range_ } from "../common";
import { elevation } from "../elevation";
import {
  groundBearing,
  groundDistance,
  indicatedToTrueAirspeed,
  move,
  toRadians,
} from "../math";
import type { Drop } from "../model";
import { positionEmpty } from "../position";
import { scaleOpacity } from "./color";

export type RangeProps = {
  drop?: Drop;
};

export const range = createLayerType<RangeProps>((context, { drop }) => {
  const [warning, setWarning] = signal<Vec3[][]>([]);
  const [alert, setAlert] = signal<Vec3[][]>([]);

  const visible = $(() => !positionEmpty(resolve(drop)?.drop ?? [0, 0, 0]));

  effect(() => {
    const _ = resolve(drop);
    if (_) void calculateWarning(_).then(setWarning);
  });

  effect(() => {
    const _ = resolve(drop);
    if (_) void calculateAlert(_).then(setAlert);
  });

  return createLayer(
    context,
    container({
      layers: [
        polygon({
          rings: warning,
          color: $(() => scaleOpacity([1, 1, 0, 0.1], visible() ? 1 : 0)),
          depth: false,
          pickable: false,
        }),
        polygon({
          rings: alert,
          color: $(() => scaleOpacity([1, 0, 0, 0.1], visible() ? 1 : 0)),
          depth: false,
          pickable: false,
        }),
      ],
    }),
  );
});

const count = 64;
const rangeShape = async (drop: Drop) => {
  const { start, end, dropAltitude, windSpeed, windDirection, glideSlope } =
    drop;
  if (positionEmpty(start) || positionEmpty(end)) return [];
  const bearing = groundBearing(start, end);
  const length = groundDistance(start, end);
  const position = move(start, length / 2, bearing);
  const [lon, lat] = position;
  const ground = await elevation([lon, lat]);
  const altitudeDifference = dropAltitude - ground;
  const speed = calculateBestGlideSpeed(drop);
  const trueAirspeed = indicatedToTrueAirspeed(
    speed,
    (dropAltitude + ground) / 2,
  );

  return range_(0, count)
    .map(i => (i / count) * 2 * Math.PI)
    .map(bearing => {
      if (altitudeDifference <= 0) return position;
      const alpha = bearing - toRadians(windDirection);
      const headwind = windSpeed * Math.cos(alpha);
      const crosswind = windSpeed * Math.sin(alpha);
      const groundSpeed =
        Math.sqrt(trueAirspeed ** 2 - crosswind ** 2) - headwind;
      if (!Number.isFinite(groundSpeed)) return position;
      const distance =
        groundSpeed * ((altitudeDifference * glideSlope) / trueAirspeed);
      if (!Number.isFinite(distance)) return position;
      if (distance < 0) return position;
      return move(position, -distance, bearing);
    });
};

const calculateWarning = async (drop: Drop) => [
  await rangeShape(drop),
  (await rangeShape({ ...drop, glideSlope: 0.8 * drop.glideSlope })).reverse(),
];

const calculateAlert = async (drop: Drop) => [
  await rangeShape({ ...drop, glideSlope: 2 * drop.glideSlope }),
  (await rangeShape(drop)).reverse(),
];
